import * as React from 'react';
import { Control, Errors } from 'react-redux-form';
import { RouteComponentProps } from 'react-router';

// Models
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { ContractSimpleViewModel } from '@models/Contract';
import { OfficeSearchViewModel, PageViewModel } from '@models/common.models';
import { Role, Roles } from '@models/Roles';

// Form validation
import { parsePhoneNumber } from '@common/parsers';
import { getDefaultErrorMessages, hasAlpha, hasValue, isValidEmail, isValidPhoneNumber } from '@common/validators';
import { Option } from 'react-select';

// Components
import { TypeAheadComponent, CheckboxInline, Select, Breadcrumbs } from '@common/components';
// import { RealEstateAgentViewOnlyComponent } from './realestate-agent-details/realestate-agent-details.component';
import { DynamicSelectIdentifier } from '@common/components/dynamic-select/dynamic-select-types';
import { FormWrapper } from '@common/components/form-wrapper/form-wrapper.component';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface RealEstateAgentComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly submit: (formData: RealEstateAgentViewModel, isModal: boolean) => Promise<void>;
  readonly errorMessage: string;
  readonly id?: number;
  readonly isModal: boolean;
  readonly isViewOnly?: boolean;
  readonly viewOnlyContracts: PageViewModel<ContractSimpleViewModel>;
  readonly toggleViewOnly: Function;
  readonly entityId?: number;
  readonly saving?: boolean;
  readonly getRealEstateAgentInfo: (id?: number) => Promise<void>;
  readonly officeLookup: (query: string, id?: number) => Promise<PageViewModel<OfficeSearchViewModel>>;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  // readonly searchContractsByAgentId: (id?: number) => void;
  // readonly getCustomerDetails: (id: number) => Promise<CustomerViewModel>;
}

// Component definition
export class RealEstateAgentComponent extends React.Component<RealEstateAgentComponentProperties> {
  constructor(props: RealEstateAgentComponentProperties) {
    super(props);
  }

  componentWillMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.getRealEstateAgentInfo(this.props.match.params.id);
    } else if (this.props.entityId) {
      this.props.getRealEstateAgentInfo(this.props.entityId);
    } else {
      this.props.getRealEstateAgentInfo();
    }
  }

  renderError() {
    if (!this.props.errorMessage) {
      return <div />;
    }
    return <div className="error">{this.props.errorMessage}</div>;
  }

  renderHideSave() {
    if (this.props.isModal) {
      return (
        <div>
          <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
            {this.props.saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      );
    }

    return (
      <div className="row text-right pull-right end-xs mt-10 mb-10">
        <button className="btn btn-secondary" type="button" onClick={() => window.history.back()}>
          Cancel
        </button>
        <button className="btn btn-primary" type="submit" disabled={this.props.saving} ref={this.props.buttonRef}>
          {this.props.saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  renderOfficeSearch(suggestion: Option<OfficeSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'}</div>
        <div>Address: {suggestion.address ? suggestion.address : '—'}</div>
        <div>City: {suggestion.city ? suggestion.city : '—'}</div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
      </div>
    );
  }

  renderSuccessID() {
    if (this.props && this.props.id && !this.props.errorMessage) {
      return <div>Agent created: ID - {this.props.id}</div>;
    }
    return;
  }

  onSubmit(formData: RealEstateAgentViewModel) {
    this.props.submit(formData, this.props.isModal);
  }

  render() {
    // if (this.props.match && this.props.match.params && this.props.match.params.id && this.props.isViewOnly) {
    //   return (
    //     <div>
    //       <RealEstateAgentViewOnlyComponent realEstateAgent={this.props.loadedRealEstateAgent} realEstateOffice={this.props.loadedAgentOffice} getContractsForAgent={this.props.searchContractsByAgentId} contractsForAgent={this.props.viewOnlyContracts} getBuyerForContract={this.props.getCustomerDetails} />
    //     </div>
    //   );
    // }
    //

    return (
      <div>
        {this.props.isModal ? null : <Breadcrumbs />}
        <h2>Real Estate Agent</h2>
        <FormWrapper
          shouldFormReset={true}
          model="realEstateAgentForm"
          className="form-horizontal"
          onSubmit={(formData: RealEstateAgentViewModel) => this.onSubmit(formData)}
          hideNativeErrors={true}
        >
          <Errors className="error error-pad" model="realEstateAgentForm" show={true} />

          <div className="row">
            <div className="form-control col-xs-4">
              <label htmlFor="realEstateAgentForm.name">Name</label>
              <Control.text model=".name" validators={{ hasAlpha: hasAlpha, required: hasValue }} maxLength={40} />
              <Errors
                className="error error-pad"
                model=".name"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor=".emailAddress">Email Address</label>
              <Control.text
                model=".emailAddress"
                validators={{ isValidEmail: isValidEmail, required: hasValue }}
                maxLength={50}
              />
              <Errors
                className="error error-pad"
                model=".emailAddress"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor=".realEstateOffice.id">Realty Office</label>
              <Control
                className="mt-10"
                model=".realEstateOffice.id"
                component={(props: { value: ''; onChange: Function }) => {
                  return (
                    <TypeAheadComponent
                      {...props}
                      onChange={props.onChange}
                      renderValue={this.renderOfficeSearch}
                      valueKey={'id'}
                      labelKey={'name'}
                      getSuggestions={this.props.officeLookup}
                      value={props.value}
                    />
                  );
                }}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".realEstateOffice.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-control col-xs-4">
              <label htmlFor="realEstateAgentForm.directPhone">Phone #</label>
              <Control.text
                model=".directPhone"
                parser={parsePhoneNumber}
                validators={{ required: hasValue, isValidPhoneNumber: isValidPhoneNumber }}
              />
              <Errors
                className="error error-pad"
                model=".directPhone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor="realEstateAgentForm.directFax">Fax #</label>
              <Control.text model=".directFax" parser={parsePhoneNumber} validators={{ isFax: isValidPhoneNumber }} />
              <Errors
                className="error error-pad"
                model=".directFax"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor="realEstateAgentForm.licenseNumber">License #</label>
              <Control.text model=".licenseNumber" maxLength={50} />
              <Errors
                className="error error-pad"
                model=".licenseNumber"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-4">
              <label htmlFor="realEstateAgentForm.agentType.id">Agent Type</label>
              <Select
                model=".agentType.id"
                identifier={DynamicSelectIdentifier.RealEstateAgentTypes}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".agentType.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="realEstateAgentForm.agentRating.id">Agent Rating</label>
              <Select
                model=".agentRating.id"
                identifier={DynamicSelectIdentifier.RealEstateAgentRatings}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".agentRating.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="realEstateAgentForm.agentStatus.id">Agent Status</label>
              <Select
                model=".agentStatus.id"
                disabled={!Role.CheckRoles([Roles.Admin, Roles.IBSAdmin, Roles.IBSManager])}
                identifier={DynamicSelectIdentifier.RealEstateAgentStatuses}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".agentStatus.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row mb-10 pb-10">
            <div className="col-md-3">
              <CheckboxInline>
                <Control.checkbox model=".onlineOrders" />
                <label className="checkbox-inline" htmlFor="realEstateAgentForm.onlineOrders">
                  {' '}
                  Online Orders
                </label>
              </CheckboxInline>
            </div>
          </div>
          <div className="row mt-10">
            <div className="col-md-7">
              <label>Contact Preference</label>
              <div className="row">
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".usps" />
                    <label className="checkbox-inline" htmlFor="realEstateAgentForm.usps">
                      USPS
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".fax" />
                    <label className="checkbox-inline" htmlFor="realEstateAgentForm.fax">
                      {' '}
                      Fax
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".email" />
                    <label className="checkbox-inline" htmlFor="realEstateAgentForm.email">
                      {' '}
                      Email
                    </label>
                  </CheckboxInline>
                </div>
              </div>
            </div>
          </div>
          {this.renderHideSave()}
        </FormWrapper>
        {/* <div>{this.renderError()}{this.renderSuccessID()}</div> */}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/realestate-agent/_components/realestate-agent.component.tsx